import type { DmLevel } from '@change-corgi/middle-end-gql-schema';

const OUTREACH_ELIGIBLE_LEVELS: readonly DmLevel[] = ['LOCAL_CITY_COUNTY', 'LOCAL_STATE'];

/**
 * Whether a petition is in the supporter → decision maker outreach audience: it has a local
 * (city/county) or state decision maker. Shared by every surface that mounts the contact flow, so
 * they cannot disagree on who qualifies.
 *
 * Fails closed: `undefined` (the metadata fetch failed) and an empty list (DMI has not classified the
 * petition yet) both read as ineligible.
 */
export function isOutreachEligiblePetition(levels: readonly DmLevel[] | undefined): boolean {
	return !!levels?.some((level) => OUTREACH_ELIGIBLE_LEVELS.includes(level));
}

type OutreachCandidate = { isContactable: boolean; level: DmLevel | null };

/**
 * Whether this decision maker's level is one the outreach ask may name: local (city/county) or state,
 * never national. Level only: it says yes to one who opted out of messages. The full rule is
 * `pickOutreachDecisionMaker` below.
 */
export function hasOutreachEligibleLevel({ level }: Pick<OutreachCandidate, 'level'>): boolean {
	return !!level && OUTREACH_ELIGIBLE_LEVELS.includes(level);
}

/**
 * The decision maker the outreach ask names: the first contactable local or state one, in the
 * petition's order. A petition passes `isOutreachEligiblePetition` as soon as any decision maker is
 * local or state, so without this a federal decision maker sorted first would be the one named.
 */
export function pickOutreachDecisionMaker<T extends OutreachCandidate>(decisionMakers: readonly T[]): T | undefined {
	return decisionMakers.find((dm) => dm.isContactable && hasOutreachEligibleLevel(dm));
}
